import Link from "next/link";
import Image from "next/image";
import type { Vehicle } from "@/lib/types";
import { calculatePrice, computeBillableDays, getMinDailyPrice } from "@/lib/pricing";
import type { VehicleCarryParams, DateRange } from "./VehicleGrid";

const FUEL_LABELS: Record<string, string> = {
  gasoline: "Benzinas",
  diesel: "Dyzelinas",
  electric: "Elektra",
  hybrid: "Hibridas",
  lpg: "Dujos",
};

const TRANSMISSION_LABELS: Record<string, string> = {
  automatic: "Automatinė",
  manual: "Mechaninė",
};

interface VehicleCardProps {
  vehicle: Vehicle;
  dateRange?: DateRange | null;
  carry?: VehicleCarryParams;
  blurDataURL?: string;
  version?: number;
  // Asset host, resolved at runtime by the server page and threaded down. This
  // card renders inside the client-side grid (HomeFleet/VehicleGrid are "use
  // client"), so reading process.env here would give the build-time value, not
  // the per-env runtime host (the CDN on prod).
  assetBaseUrl: string;
}

function buildHref(slug: string, dateRange?: DateRange | null, carry?: VehicleCarryParams): string {
  const params = new URLSearchParams();
  if (dateRange) {
    params.set("from", dateRange.from);
    params.set("to", dateRange.to);
  }
  if (carry) {
    if (carry.pickup_time) params.set("pickup_time", carry.pickup_time);
    if (carry.return_time) params.set("return_time", carry.return_time);
    if (carry.pickup_location_id != null) params.set("pickup_location_id", String(carry.pickup_location_id));
    if (carry.return_location_id != null) params.set("return_location_id", String(carry.return_location_id));
  }
  const qs = params.toString();
  return qs ? `/vehicles/${slug}?${qs}` : `/vehicles/${slug}`;
}

export default function VehicleCard({ vehicle, dateRange, carry, blurDataURL, version, assetBaseUrl }: VehicleCardProps) {
  const imageUrl = vehicle.image
    ? `${assetBaseUrl}/assets/${vehicle.image}${version ? `?v=${version}` : ""}`
    : null;

  const href = buildHref(vehicle.slug, dateRange, carry);

  const tierPrice = dateRange
    ? calculatePrice(
        computeBillableDays(
          dateRange.from,
          dateRange.to,
          carry?.pickup_time ?? "12:00",
          carry?.return_time ?? "12:00",
        ),
        vehicle,
      ).pricePerDay
    : null;

  return (
    <Link
      href={href}
      className="group block bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-lg hover:border-gray-300 transition-all duration-200"
    >
      <div className="relative aspect-[16/10] bg-gray-100">
        {imageUrl ? (
          <Image
            src={imageUrl}
            alt={`${vehicle.make} ${vehicle.model}`}
            fill
            className="object-cover group-hover:scale-105 transition-transform duration-300"
            sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
            quality={75}
            placeholder={blurDataURL ? "blur" : "empty"}
            blurDataURL={blurDataURL}
          />
        ) : (
          <div className="flex items-center justify-center h-full text-gray-400">
            <svg
              className="w-16 h-16"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1}
                d="M9 17a2 2 0 11-4 0 2 2 0 014 0zM19 17a2 2 0 11-4 0 2 2 0 014 0z"
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1}
                d="M13 16V6a1 1 0 00-1-1H4a1 1 0 00-1 1v10M17 16l2-5h-4l-2-4H5"
              />
            </svg>
          </div>
        )}
      </div>

      <div className="p-4">
        <h3 className="text-lg font-semibold text-gray-900">
          {vehicle.make} {vehicle.model}{" "}
          <span className="text-gray-500 font-normal">{vehicle.year}</span>
        </h3>

        <div className="flex flex-wrap gap-2 mt-3">
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
            {TRANSMISSION_LABELS[vehicle.transmission] || vehicle.transmission}
          </span>
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
            {FUEL_LABELS[vehicle.fuel_type] || vehicle.fuel_type}
          </span>
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
            {vehicle.seats} vietų
          </span>
        </div>

        <div className="mt-4 flex items-baseline justify-end gap-1">
          {tierPrice == null && <span className="text-xs text-gray-500">nuo</span>}
          <span className="text-xl font-bold text-gray-900">
            {tierPrice ?? getMinDailyPrice(vehicle)}&nbsp;&euro;
          </span>
          <span className="text-sm text-gray-500">/ dienai</span>
        </div>
      </div>
    </Link>
  );
}
